import { useCallback, useEffect, useRef } from 'react';
import { prefetch } from 'vike/client/router';

import {
  RELEASE_FEED_INPUT,
  RELEASE_FEED_KEY,
  releaseFeedInitialQuery,
} from '@/pages/(guarded)/release-center/components/releaseFeed';
import { infiniteStoreKey, useOptionalPrefetchInfinite } from './api/prefetchInfinite';
import { prefetchRequest } from './api/requestMemo';
import { isPreviewModeActive } from './previewMode';
import useMe from './useMe';
import { MEMBERSHIPS_QUERY } from './useMemberships';

/**
 * Start loading a page before the tap or click that opens it lands: its code
 * (Vike's prefetch) and its primary data, warmed into the same stores the page's
 * hooks read (the infinite store, requestMemo), under the same keys and queries,
 * so the page paints that data on its first frame and sends no second request.
 *
 * Called from the mobile tab bar on press and from the desktop sidebar on hover
 * intent; not from every link on screen.
 */

// A press and the click that follows (or a hover that lingers) ask for the same
// page more than once; one prefetch per page in this window is plenty.
const REPEAT_MS = 5000;
const lastPrefetch = new Map<string, number>();

export function usePrefetchPage() {
  const me = useMe();
  const prefetchInfinite = useOptionalPrefetchInfinite();
  const meRef = useRef(me);
  meRef.current = me;

  return useCallback(
    (href: string) => {
      const path = href.split(/[?#]/)[0];
      const now = Date.now();
      if (now - (lastPrefetch.get(path) ?? 0) < REPEAT_MS) {
        return;
      }
      lastPrefetch.set(path, now);

      prefetch(href).catch(() => {
        // Not a client-routable page, or offline: the navigation loads it as usual.
      });

      const user = meRef.current;
      if (!user) {
        return;
      }

      switch (path) {
        case '/release-center':
          prefetchInfinite(
            infiniteStoreKey(RELEASE_FEED_KEY),
            RELEASE_FEED_INPUT,
            releaseFeedInitialQuery(user.role),
          );
          prefetchInfinite(infiniteStoreKey(`memberships:${user.id}`), '/api/v1/membership/me', {
            ...MEMBERSHIPS_QUERY,
            order: { by: 'id', dir: 'DESC' },
          });
          break;
        case '/results':
          // The check that picks My or All results gates the whole page body.
          prefetchRequest('/api/v1/messageReceipt/stats/me', { count: true });
          break;
        case '/order':
          // OrderPageWeb's two product requests, exactly as it sends them on /order.
          if (!isPreviewModeActive()) {
            prefetchRequest('/api/v1/product', { data: {}, page: { limit: 100 } });
            prefetchRequest('/api/v1/product', { page: { limit: 100 } });
          }
          break;
      }
    },
    [prefetchInfinite],
  );
}

/**
 * Hover intent for a desktop link: prefetch once the pointer has rested on it for
 * `delay` ms, or on keyboard focus. Mouse only, so a touch never prefetches twice
 * (the tab bar handles touch on press).
 */
export function usePrefetchOnHover(href: string | undefined, delay = 100) {
  const prefetchPage = usePrefetchPage();
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const cancel = useCallback(() => clearTimeout(timer.current), []);

  const onPointerEnter = useCallback(
    (event: React.PointerEvent) => {
      if (!href || event.pointerType !== 'mouse') {
        return;
      }
      clearTimeout(timer.current);
      timer.current = setTimeout(() => prefetchPage(href), delay);
    },
    [href, delay, prefetchPage],
  );

  const onFocus = useCallback(() => {
    if (href) {
      prefetchPage(href);
    }
  }, [href, prefetchPage]);

  return { onPointerEnter, onPointerLeave: cancel, onFocus };
}
